export function getPatternStyle(pattern) {
  if (!pattern) return {};

  const looksLikeCssBackgroundValue = (value) => {
    if (typeof value !== 'string') return false;
    // Accept common CSS background/background-image syntaxes.
    return /(gradient\(|url\(|image-set\(|repeating-|var\(|calc\()/i.test(value);
  };

  const extractInlineCssValue = (html, propertyName) => {
    if (typeof html !== 'string' || !html) return '';
    // Very small parser: find the first "prop: value" occurrence.
    const escaped = propertyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = html.match(new RegExp(`${escaped}\\s*:\\s*([^;"']+)`, 'i'));
    return match?.[1]?.trim() || '';
  };

  const extractHexBgFromTailwindClass = (html) => {
    if (typeof html !== 'string' || !html) return '';
    const match = html.match(/\bbg-\[#([0-9a-fA-F]{3,8})\]\b/);
    if (!match) return '';
    return `#${match[1]}`;
  };

  const style = {};

  const code = typeof pattern.code === 'string' ? pattern.code : '';

  // Ensure patterns that use transparency still render on an intended base.
  // Prefer explicit backgroundColor, else try to infer from Tailwind class like bg-[#0a0a0a].
  const inferredBg = extractHexBgFromTailwindClass(code);
  if (pattern.backgroundColor) {
    style.backgroundColor = pattern.backgroundColor;
  } else if (inferredBg) {
    style.backgroundColor = inferredBg;
  } else if (pattern.category === 'dark') {
    style.backgroundColor = '#1e293b';
  } else {
    style.backgroundColor = '#ffffff';
  }

  // Background: prefer explicit dataset fields, but avoid applying descriptive placeholders.
  // 1) pattern.style (if user pasted "style: {...}")
  if (pattern.style && typeof pattern.style === 'object') {
    const allowedKeys = [
      'background',
      'backgroundColor',
      'backgroundImage',
      'backgroundSize',
      'backgroundPosition',
      'backgroundRepeat',
      'backgroundBlendMode',
      'maskImage',
      'WebkitMaskImage',
      'maskComposite',
      'WebkitMaskComposite',
      'mask',
      'WebkitMask',
      'filter',
      'opacity',
      'mixBlendMode',
      'boxShadow',
      'animation',
      'imageRendering',
    ];

    for (const key of allowedKeys) {
      if (pattern.style[key] != null) style[key] = pattern.style[key];
    }
  }

  // 2) canonical dataset field: css (intended for background-image)
  if (!style.background && !style.backgroundImage && looksLikeCssBackgroundValue(pattern.css)) {
    style.backgroundImage = pattern.css;
  }

  // 3) fallback: try to extract from pattern.code (many of the newly added entries embed real CSS here)
  if (!style.background && !style.backgroundImage && code) {
    const bg = extractInlineCssValue(code, 'background');
    const bgImage = extractInlineCssValue(code, 'background-image');

    if (looksLikeCssBackgroundValue(bg)) {
      style.background = bg;
    } else if (looksLikeCssBackgroundValue(bgImage)) {
      style.backgroundImage = bgImage;
    }

    const bgSize = extractInlineCssValue(code, 'background-size');
    const bgPos = extractInlineCssValue(code, 'background-position');
    const bgRepeat = extractInlineCssValue(code, 'background-repeat');
    const bgBlend = extractInlineCssValue(code, 'background-blend-mode');

    if (bgSize) style.backgroundSize = bgSize;
    if (bgPos) style.backgroundPosition = bgPos;
    if (bgRepeat) style.backgroundRepeat = bgRepeat;
    if (bgBlend) style.backgroundBlendMode = bgBlend;
  }

  if (pattern.backgroundSize) style.backgroundSize = pattern.backgroundSize;
  if (pattern.backgroundPosition) style.backgroundPosition = pattern.backgroundPosition;
  if (pattern.backgroundRepeat) style.backgroundRepeat = pattern.backgroundRepeat;
  if (pattern.backgroundBlendMode) style.backgroundBlendMode = pattern.backgroundBlendMode;

  // Masks (used by fade/grid patterns)
  if (pattern.maskImage) style.maskImage = pattern.maskImage;
  if (pattern.WebkitMaskImage) style.WebkitMaskImage = pattern.WebkitMaskImage;
  if (pattern.maskComposite) style.maskComposite = pattern.maskComposite;
  if (pattern.WebkitMaskComposite) style.WebkitMaskComposite = pattern.WebkitMaskComposite;
  if (pattern.mask) style.mask = pattern.mask;
  if (pattern.WebkitMask) style.WebkitMask = pattern.WebkitMask;

  // Misc rendering controls
  if (pattern.filter) style.filter = pattern.filter;
  if (pattern.opacity != null) style.opacity = pattern.opacity;
  if (pattern.mixBlendMode) style.mixBlendMode = pattern.mixBlendMode;
  if (pattern.boxShadow) style.boxShadow = pattern.boxShadow;
  if (pattern.animation) style.animation = pattern.animation;
  if (pattern.imageRendering) style.imageRendering = pattern.imageRendering;

  return style;
}
